/* Warehouse Bold — the pre-type search panel, and the device history it runs on.
 *
 * Three jobs, all device-local; nothing here talks to a server:
 *   1. On a product page, record {url, title, img, price, currency} into wb:rv.
 *      ⚠ the base theme's own hypertheme:recently-viewed stores bare product IDs, and
 *      there is no storefront endpoint that resolves an id to a product —
 *      which is why this keeps its own record instead of reading the base theme's.
 *   2. Record submitted searches into wb:rq (on submit and on suggestion
 *      clicks), capped at 5, most recent first.
 *   3. When the search field gains focus with an EMPTY query, render both
 *      stores into the pre-type panel and open it. First keystroke hides it
 *      and the base theme's own predictive results take over; clearing brings it back.
 *
 * search.js is NOT modified for the open: its onFocus already adds
 * `predictive-search-open` to the element unconditionally — the only things
 * it gates behind its recommendations setting are the body class and the
 * content unhide, and this file supplies those. Closing is untouched: the
 * outside-click close (with the no-overlay case fixed in d4f4976) already
 * removes everything this adds.
 */
(() => {
  'use strict';

  var RV_KEY = 'wb:rv';
  var RQ_KEY = 'wb:rq';

  var read = function (key) {
    try { return JSON.parse(localStorage.getItem(key)) || []; } catch (e) { return []; }
  };
  var write = function (key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* private mode */ }
  };

  /* ---------------- 1. record product views ---------------- */

  var recordView = function () {
    var root = document.querySelector('.wb-product[data-product-id]');
    if (!root) return; // not a WB product page
    var title = document.querySelector('meta[property="og:title"]');
    var img = document.querySelector('meta[property="og:image"], meta[property="og:image:secure_url"]');
    var price = document.querySelector('[data-wb-price][data-unit-cents]');
    var cents = price ? parseInt(price.getAttribute('data-unit-cents'), 10) : NaN;
    var entry = {
      u: root.getAttribute('data-url') || location.pathname,
      t: title ? title.content : document.title.split('–')[0].trim(),
      i: img ? img.content : '',
      c: isNaN(cents) ? null : cents,
      // The currency those cents are in. A view on one market must not be
      // priced in another market's currency (154.00 AUD shown as "$154.00 USD").
      k: (window.Shopify && Shopify.currency && Shopify.currency.active) || ''
    };
    if (!entry.t) return;
    var list = read(RV_KEY).filter(function (e) { return e.u !== entry.u; });
    list.unshift(entry);
    write(RV_KEY, list.slice(0, 8));
  };

  /* ---------------- 2. record searches ---------------- */

  var recordQuery = function (q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    var lower = q.toLowerCase();
    var list = read(RQ_KEY).filter(function (e) { return e.toLowerCase() !== lower; });
    list.unshift(q);
    write(RQ_KEY, list.slice(0, 5));
  };

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/search"]')) return;
    var input = form.querySelector('input[name="q"]');
    if (input) recordQuery(input.value);
  }, true);

  document.addEventListener('click', function (e) {
    // A tapped suggestion is a search the shopper meant.
    var a = e.target.closest && e.target.closest('.wb-ps__chip, .wb-ps__qlist a');
    if (!a) return;
    try {
      var q = new URL(a.href, location.origin).searchParams.get('q');
      if (q) recordQuery(q);
    } catch (err) { /* not a search url — a collection link, fine */ }
  }, true);

  /* ---------------- 3. the pre-type panel ---------------- */

  /* Outside the AU market the ISO code follows the amount, exactly as on the
     server-rendered rows (snippets/wb-money.liquid): a bare "$" is ambiguous
     between AUD, USD, NZD and the rest. narrowSymbol keeps "$100.00 USD"
     rather than "US$100.00 USD"; the AU branch is untouched. */
  var AWAY = !!(window.Shopify && Shopify.country && Shopify.country !== 'AU');
  var CUR = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';

  var money = function (cents) {
    if (cents == null) return '';
    try {
      var opts = { style: 'currency', currency: CUR };
      if (AWAY) opts.currencyDisplay = 'narrowSymbol';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', opts).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // HTML, not text: the code gets its own quieter span, like wb-money.liquid.
  var moneyHTML = function (cents) {
    var amount = esc(money(cents));
    return AWAY ? amount + '&nbsp;<span class="wb-cur">' + esc(CUR) + '</span>' : amount;
  };

  var esc = function (s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  };

  /* og:image arrives at whatever size the social tag uses; ask the CDN for a
     480px derivative so a retina tile is sharp without shipping a poster. */
  var sized = function (src) {
    try {
      var u = new URL(src, location.origin);
      if (/cdn\.shopify|\/cdn\//.test(u.href)) { u.searchParams.set('width', '480'); return u.toString(); }
      return src;
    } catch (e) { return src; }
  };

  var CLOCK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>' +
    '<path d="M12 7v5l3.2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  var mount = function (ps) {
    if (ps.__wbPreBound) return;
    ps.__wbPreBound = true;

    var input = ps.querySelector('input[type="search"]');
    var content = ps.querySelector('.search__content');
    var pre = ps.querySelector('[data-wb-pre]');
    var results = ps.querySelector('.search__results');
    if (!input || !content || !pre) return;

    /* ⚠ There are TWO predictive-search instances on /search — the header one
       and main-search.liquid's on-page box — and `body.search-open` belongs to
       the HEADER overlay alone. search.js only ever clears it for the header
       instance (toggleSearchState, 215-217), so adding it from the on-page box
       left the search results page permanently scroll-locked (overflow:hidden)
       under a dimming overlay, with nothing able to release it. */
    var isHeader = ps.closest('header') !== null;

    var renderPre = function () {
      var rq = read(RQ_KEY);
      // ⚠ Never show the page the shopper is already on — with one product in
      // history, "pick up where you left off" was pointing at the page under
      // their feet (Owen, 2026-08-16).
      var rv = read(RV_KEY).filter(function (e) { return e.u !== location.pathname; });
      var recentList = pre.querySelector('[data-wb-pre-recent]');
      var recentHead = pre.querySelector('[data-wb-pre-recent-h]');
      var rvList = pre.querySelector('[data-wb-pre-rv]');
      var rvHead = pre.querySelector('[data-wb-pre-rv-h]');

      if (recentList) {
        recentList.innerHTML = rq.map(function (q) {
          return '<li role="none"><a href="/search?type=product&options%5Bprefix%5D=last&q=' +
            encodeURIComponent(q) + '">' + CLOCK + '<span>' + esc(q) + '</span></a></li>';
        }).join('');
        if (recentHead) recentHead.hidden = !rq.length;
      }
      if (rvList) {
        rvList.innerHTML = rv.slice(0, 4).map(function (p) {
          // Price only in the currency it was recorded in. Entries from before
          // the currency was stored are assumed AUD, as they always were, so
          // they keep their price on the AU market and drop it elsewhere.
          var priced = p.c != null && (p.k ? p.k === CUR : !AWAY);
          return '<li role="none"><a class="wb-ps__prod" href="' + esc(p.u) + '">' +
            '<span class="wb-ps__tile">' + (p.i ? '<img src="' + esc(sized(p.i)) + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="wb-ps__mid"><span class="wb-ps__t">' + esc(p.t) + '</span></span>' +
            (priced ? '<span class="wb-ps__price">' + moneyHTML(p.c) + '</span>' : '') +
            '</a></li>';
        }).join('');
        if (rvHead) rvHead.hidden = !rv.length;
      }
      // Popular chips are server-rendered; the panel has something to say if
      // ANY of the three sections does.
      var hasServerBits = !!pre.querySelector('.wb-ps__chiprow, [data-wb-pre-related], [data-wb-pre-more]');
      // Everything lives in the rail except More-in/Popular — when that is
      // absent, let the rail be the whole panel rather than sit beside a void.
      var cols = pre.querySelector('.wb-ps__cols');
      if (cols) cols.classList.toggle('wb-ps__cols--railonly', !pre.querySelector('[data-wb-pre-more]'));
      return !!(rq.length || rv.length || hasServerBits);
    };

    var showPre = function () {
      if (!renderPre()) return; // nothing to show — behave exactly like today
      pre.hidden = false;
      if (results) results.classList.add('hidden');
      content.classList.remove('hidden');
      if (isHeader) document.body.classList.add('search-open');
      ps.classList.add('predictive-search-open');
    };
    var hidePre = function () {
      pre.hidden = true;
      if (results) results.classList.remove('hidden');
    };

    /* Typing state. Pairs with the theme's [results] attribute to drive the
       skeleton: [results] says whether anything is on screen, this says whether
       the shopper is asking for something new. It exists ONLY to supply the
       instant edge — search.js debounces its fetch by 300ms, so waiting for the
       theme's own [loading] would leave the panel empty for those 300ms.
       ⚠ Also opens the panel: on the very first keystroke from a closed field
       (focus fired before the panel had anything to show) nothing else would,
       and the shopper would type into a void until the response landed. */
    var typing = function (on) {
      content.classList.toggle('wb-ps-typing', !!on);
      if (on) {
        content.classList.remove('hidden');
        if (isHeader) document.body.classList.add('search-open');
        ps.classList.add('predictive-search-open');
      }
    };

    input.addEventListener('focus', function () {
      if (!input.value.trim()) showPre();
    });
    var preTimer;
    input.addEventListener('input', function () {
      clearTimeout(preTimer);
      if (input.value.trim()) { hidePre(); typing(true); return; }
      typing(false);
      showPre();
      /* ⚠ Then show it AGAIN once search.js has had its say. Its onChange is
         debounced 300ms; on an empty field that calls clear() ->
         toggleSearchState(false), which re-adds `hidden` to the content wrapper
         on a store with no search recommendations — so backspacing to empty
         showed the pre-panel and then collapsed it to nothing 300ms later.
         (Clicking the X doesn't hit this: clear() runs synchronously on the
         click, before the reset handler below re-shows the panel.)
         Only re-assert if the shopper is still sitting in the empty field —
         otherwise a click away within the window would yank the panel back. */
      preTimer = setTimeout(function () {
        if (document.activeElement === input && !input.value.trim()) showPre();
      }, 380);
    });
    /* Clear the flag from the theme's own signal: search.js sets [results] the
       moment rendered markup is on screen, and clears it in clear().
       ⚠ This replaced a MutationObserver that watched the results box for
       .wb-ps. That observer worked, but the CSS beside it asked the box the
       same question — and the box LIES after a clear: clear() empties the field
       and leaves the old query's markup behind, so every search after the first
       looked "already answered" and the skeleton never appeared (Owen,
       2026-08-16). One signal, owned by the theme, asked in one place. */
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (ps.hasAttribute('results')) typing(false);
      }).observe(ps, { attributes: true, attributeFilter: ['results'] });
    }
    // the base theme's reset button clears the field: bring the panel back.
    var reset = ps.querySelector('button[type="reset"]');
    if (reset) reset.addEventListener('click', function () {
      typing(false);
      setTimeout(showPre, 0);
    });
  };

  var mountAll = function () {
    document.querySelectorAll('predictive-search').forEach(mount);
  };

  var init = function () {
    recordView();
    /* ⚠ Mount only once <predictive-search> is UPGRADED, so the theme's own
       focus listener (registered in its constructor) always runs before ours.
       header.liquid skips search.js on /search and main-search.liquid loads it
       instead — after the header group — so on that ONE template this file runs
       first, our focus handler fired first, and search.js then re-hid the
       content wrapper our pre-type panel had just opened: the panel was dead on
       /search and nowhere else. Waiting for the upgrade makes the listener
       order identical on every template. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('predictive-search').then(mountAll);
    } else {
      mountAll();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('predictive-search').forEach(mount);
  });
})();
